///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Saved Views browser window: thumbnail grid with search + tag filter,
/// inline rename/re-tag, delete, fly-to, sorted by recency. Also hosts
/// the auto-orbit settings row (the switch's only home until settings
/// grow elsewhere). Tour authoring lands here in a later phase.
///

import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { Switch } from '@/components/ui/switch'
import { ViewThumbnailStore } from '@/earthbrowser/camera-director/ViewThumbnailStore'
import { isMobile } from '@/earthbrowser/utils/platform'
import { useViewThumbnail } from '@/hooks/useViewThumbnail'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'
import { sendCameraCommand, setTourSettings } from '@/redux/earthBrowserSlice'
import { deleteView, renameView, setViewTags, type SavedView } from '@/redux/savedViewsSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useShareLink } from '@/hooks/useShareLink'
import { viewEmbedSnippet } from '@/utils/embedSnippet'
import { cEmbedCodeEnabled } from '@/utils/featureGates'
import { relativeTime, saveCurrentView } from '@/utils/savedViews'
import { cn } from '@/utils/utils'
import { BookmarkIcon, BookmarkPlusIcon, CheckIcon, CodeXmlIcon, LinkIcon, MapPinIcon, PencilIcon, SearchIcon, TagIcon, Trash2Icon } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

const cIdleDelayChoices = [
	{ label: '30 seconds', value: 30 },
	{ label: '45 seconds', value: 45 },
	{ label: '1 minute', value: 60 },
	{ label: '2 minutes', value: 120 },
	{ label: '5 minutes', value: 300 },
]

interface ViewCardProps {
	view: SavedView
	onFlyTo: (viewId: string) => void
}

const ViewCard = memo(function ViewCard({ view, onFlyTo }: ViewCardProps) {
	const dispatch = useAppDispatch()
	const thumbnailUrl = useViewThumbnail(view.id)
	const { shareView } = useShareLink()

	const [editing, setEditing] = useState<'name' | 'tags' | null>(null)
	const [draft, setDraft] = useState('')
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const [linkCopied, setLinkCopied] = useState(false)
	const [embedCopied, setEmbedCopied] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (editing) inputRef.current?.select()
	}, [editing])

	// Disarm the delete confirmation after a moment
	useEffect(() => {
		if (!confirmingDelete) return
		const timer = setTimeout(() => setConfirmingDelete(false), 3000)
		return () => clearTimeout(timer)
	}, [confirmingDelete])

	const beginEdit = (field: 'name' | 'tags') => {
		setDraft(field === 'name' ? view.name : view.tags.join(', '))
		setEditing(field)
	}

	const commitEdit = () => {
		if (editing === 'name') {
			const name = draft.trim()
			if (name && name !== view.name) {
				dispatch(renameView({ id: view.id, name }))
			}
		} else if (editing === 'tags') {
			const tags = draft.split(',').map(t => t.trim()).filter(t => t.length > 0)
			dispatch(setViewTags({ id: view.id, tags }))
		}
		setEditing(null)
	}

	const handleDelete = () => {
		if (!confirmingDelete) {
			setConfirmingDelete(true)
			return
		}
		dispatch(deleteView(view.id))
		void ViewThumbnailStore.shared.deleteThumbnail(view.id)
	}

	// Signed in: server-backed short URL (?s=); signed out: param deep link
	const handleCopyLink = () => {
		void shareView(view).then(url => navigator.clipboard.writeText(url)).then(() => {
			setLinkCopied(true)
			setTimeout(() => setLinkCopied(false), 1500)
		})
	}

	// Copy-paste iframe embed snippet (self-contained; works signed out)
	const handleCopyEmbed = () => {
		void navigator.clipboard.writeText(viewEmbedSnippet(view)).then(() => {
			setEmbedCopied(true)
			setTimeout(() => setEmbedCopied(false), 1500)
		})
	}

	return (
		<div
			className={cn(
				"group relative flex flex-col rounded-xl overflow-hidden",
				"bg-black/30 border border-slate-700 hover:border-slate-500",
				"transition-all duration-300"
			)}
		>
			{/* Thumbnail (click to fly) */}
			<button
				onClick={() => onFlyTo(view.id)}
				className={cn(
					"relative block w-full aspect-video bg-slate-900/60",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
				)}
				title={`Fly to "${view.name}"`}
				aria-label={`Fly to ${view.name}`}
			>
				{thumbnailUrl ? (
					<img
						src={thumbnailUrl}
						alt=""
						className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
					/>
				) : (
					<div className="absolute inset-0 flex items-center justify-center text-white/25">
						<MapPinIcon className="w-8 h-8" />
					</div>
				)}
			</button>

			{/* Hover actions */}
			<div
				className={cn(
					"absolute top-1.5 right-1.5 flex gap-1",
					"opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-300"
				)}
			>
				<button
					onClick={() => beginEdit('name')}
					className="w-7 h-7 rounded-lg flex items-center justify-center bg-black/70 backdrop-blur-md text-white/80 hover:text-white hover:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
					title="Rename"
					aria-label={`Rename ${view.name}`}
				>
					<PencilIcon className="w-3.5 h-3.5" />
				</button>
				<button
					onClick={() => beginEdit('tags')}
					className="w-7 h-7 rounded-lg flex items-center justify-center bg-black/70 backdrop-blur-md text-white/80 hover:text-white hover:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
					title="Edit tags"
					aria-label={`Edit tags for ${view.name}`}
				>
					<TagIcon className="w-3.5 h-3.5" />
				</button>
				<button
					onClick={handleCopyLink}
					className={cn(
						"w-7 h-7 rounded-lg flex items-center justify-center backdrop-blur-md",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
						linkCopied
							? "bg-emerald-600/90 text-white"
							: "bg-black/70 text-white/80 hover:text-white hover:bg-black/90"
					)}
					title={linkCopied ? "Link copied" : "Copy share link"}
					aria-label={`Copy share link for ${view.name}`}
				>
					{linkCopied ? <CheckIcon className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />}
				</button>
				{cEmbedCodeEnabled && (
					<button
						onClick={handleCopyEmbed}
						className={cn(
							"w-7 h-7 rounded-lg flex items-center justify-center backdrop-blur-md",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
							embedCopied
								? "bg-emerald-600/90 text-white"
								: "bg-black/70 text-white/80 hover:text-white hover:bg-black/90"
						)}
						title={embedCopied ? "Embed code copied" : "Copy embed code"}
						aria-label={`Copy embed code for ${view.name}`}
					>
						{embedCopied ? <CheckIcon className="w-3.5 h-3.5" /> : <CodeXmlIcon className="w-3.5 h-3.5" />}
					</button>
				)}
				<button
					onClick={handleDelete}
					className={cn(
						"h-7 rounded-lg flex items-center justify-center gap-1 px-1.5 backdrop-blur-md",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500",
						confirmingDelete
							? "bg-red-600/90 text-white"
							: "bg-black/70 text-white/80 hover:text-red-300 hover:bg-black/90"
					)}
					title={confirmingDelete ? "Click again to delete" : "Delete view"}
					aria-label={confirmingDelete ? `Confirm delete ${view.name}` : `Delete ${view.name}`}
				>
					<Trash2Icon className="w-3.5 h-3.5" />
					{confirmingDelete && <span className="text-[10px] font-semibold">Delete?</span>}
				</button>
			</div>

			{/* Name / tags / edit input */}
			<div className="flex flex-col gap-1 p-2.5">
				{editing ? (
					<input
						ref={inputRef}
						value={draft}
						onChange={e => setDraft(e.target.value)}
						onBlur={commitEdit}
						onKeyDown={e => {
							if (e.key === 'Enter') commitEdit()
							if (e.key === 'Escape') setEditing(null)
						}}
						placeholder={editing === 'tags' ? 'tag1, tag2, …' : 'View name'}
						className={cn(
							"w-full px-2 py-1 text-sm rounded-lg",
							"bg-black/40 text-white border-2 border-slate-700",
							"focus:outline-none focus:border-blue-500"
						)}
					/>
				) : (
					<button
						onClick={() => onFlyTo(view.id)}
						className="text-left text-sm font-medium text-white/90 truncate hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
						title={view.name}
					>
						{view.name}
					</button>
				)}
				<div className="flex items-center gap-1.5 min-h-[18px]">
					{view.tags.map(tag => (
						<span
							key={tag}
							className="px-1.5 py-0.5 text-[10px] rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/70"
						>
							{tag}
						</span>
					))}
					<span className="ml-auto text-[10px] text-white/40">{relativeTime(view.updatedAt)}</span>
				</div>
			</div>
		</div>
	)
})

interface ViewsBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const ViewsBrowserRenderer = memo(function ViewsBrowserRenderer({
	windowId,
	windowState,
}: ViewsBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const views = useAppSelector(state => state.savedViews.views)
	const tourSettings = useAppSelector(state => state.earthbrowser.tourSettings)

	const [search, setSearch] = useState('')
	const [tagFilter, setTagFilter] = useState<string>('')

	const sorted = useMemo(() =>
		Object.values(views).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
	[views])

	const allTags = useMemo(() =>
		[...new Set(sorted.flatMap(view => view.tags))].sort(),
	[sorted])

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase()
		return sorted.filter(view => {
			if (tagFilter && !view.tags.includes(tagFilter)) return false
			if (!query) return true
			return view.name.toLowerCase().includes(query) ||
				view.tags.some(tag => tag.toLowerCase().includes(query))
		})
	}, [sorted, search, tagFilter])

	const handleFlyTo = useCallback((viewId: string) => {
		dispatch(sendCameraCommand({ command: "view/flyTo", data: { viewId } }))
		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [dispatch, windowId])

	const content = useMemo(() => (
		<div className="flex flex-col h-full bg-black/20 backdrop-blur-sm">
			{/* Toolbar: search + tag filter, auto-orbit settings, save.
			    Lives in the content (not the titlebar) so it stays visible
			    when the window is docked in the bottom drawer. */}
			<div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 border-b border-white/10">
				<div className="relative">
					<SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
					<input
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder="Search views…"
						className={cn(
							"w-40 pl-8 pr-2 py-1 text-xs rounded-full",
							"bg-black/30 text-white/90 border-2 border-slate-700",
							"focus:outline-none focus:border-blue-500 placeholder:text-white/40"
						)}
					/>
				</div>
				{allTags.length > 0 && (
					<select
						value={tagFilter}
						onChange={e => setTagFilter(e.target.value)}
						aria-label="Filter by tag"
						className={cn(
							"px-2 py-1 text-xs rounded-full cursor-pointer",
							"bg-black/30 text-white/80 border-2 border-slate-700",
							"focus:outline-none focus:border-blue-500 hover:text-white"
						)}
					>
						<option value="">All tags</option>
						{allTags.map(tag => (
							<option key={tag} value={tag}>{tag}</option>
						))}
					</select>
				)}
				<div className="flex-1" />
				<Switch
					checked={tourSettings.autoOrbit}
					onCheckedChange={checked => dispatch(setTourSettings({ autoOrbit: checked }))}
					aria-label="Auto-orbit when idle"
				/>
				<span className="text-sm text-white/80">Auto-orbit when idle</span>
				<select
					value={tourSettings.idleDelaySeconds}
					onChange={e => dispatch(setTourSettings({ idleDelaySeconds: Number(e.target.value) }))}
					disabled={!tourSettings.autoOrbit}
					aria-label="Idle delay before auto-orbit"
					className={cn(
						"px-2 py-1 text-xs rounded-full cursor-pointer",
						"bg-black/30 text-white/80 border-2 border-slate-700",
						"focus:outline-none focus:border-blue-500",
						!tourSettings.autoOrbit && "opacity-40 cursor-default"
					)}
				>
					{cIdleDelayChoices.map(choice => (
						<option key={choice.value} value={choice.value}>after {choice.label}</option>
					))}
				</select>
				<button
					onClick={() => { void saveCurrentView() }}
					className={cn(
						"flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full",
						"bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 text-white",
						"hover:scale-105 hover:shadow-xl transition-all duration-300",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
					)}
				>
					<BookmarkPlusIcon className="w-3.5 h-3.5" />
					Save Current View
				</button>
			</div>

			{/* Grid / empty state */}
			{filtered.length > 0 ? (
				<div className="flex-1 overflow-y-auto p-4">
					<div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(190px,1fr))]">
						{filtered.map(view => (
							<ViewCard key={view.id} view={view} onFlyTo={handleFlyTo} />
						))}
					</div>
				</div>
			) : (
				<div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-8">
					<BookmarkIcon className="w-10 h-10 text-white/20" />
					{sorted.length === 0 ? (
						<>
							<p className="text-sm font-medium text-white/70">No saved views yet</p>
							<p className="text-xs text-white/40 max-w-[260px]">
								Navigate somewhere interesting and press ⌘D or the bookmark
								button to save the view.
							</p>
						</>
					) : (
						<p className="text-sm text-white/60">No views match your search</p>
					)}
				</div>
			)}
		</div>
	), [dispatch, filtered, sorted.length, search, allTags, tagFilter, tourSettings, handleFlyTo])

	// Register content for bottom-drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<BookmarkIcon className="w-5 h-5" />}
			iconColor="text-cyan-400"
		>
			{content}
		</DatasetBrowserWindow>
	)
})
